import { useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { ClientAccountTypeEnum } from "@/__generated__/graphql";
import { useSafeAreaInsets } from "@/components/hooks/use-safe-area-insets";
import { AnimatedCaret } from "@/components/shared/animated-caret";
import { BottomDrawerModalAnimated } from "@/components/shared/bottom-drawer-modal-animated";
import { Divider } from "@/components/shared/divider";
import { LuceSparkle } from "@/components/shared/icons";
import { Typography } from "@/components/shared/typography";
import { Button, Card, CardContent, View } from "@/components/ui";
import { formatPrice } from "@/lib/helpers/number";
import { useAuthState } from "@/store/auth";

import type { GiftCardFormData } from "./use-gift-card-form";
import { useSubmitGiftCard } from "./use-submit-gift-card";

export function SectionGiftCardNavigationBottom() {
  const {
    handleSubmit,
    control,
    formState: { isSubmitting },
  } = useFormContext<GiftCardFormData>();

  const { onSubmit: submitGiftCard } = useSubmitGiftCard();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const insets = useSafeAreaInsets();

  const accountType = useAuthState((state) => state.data.userInfo.accountType);
  const isCorporate = accountType === ClientAccountTypeEnum.Corporate;

  const [amount, quantity] = useWatch({ control, name: ["amount", "quantity"] });
  const unitAmount = Number(amount);
  const itemCount = isCorporate ? Math.max(Number(quantity) || 0, 0) : 1;
  const totalAmount = unitAmount * itemCount;
  const hasItems = unitAmount > 0 && itemCount > 0;

  const onSubmit = handleSubmit(submitGiftCard);

  return (
    <>
      <Card
        className="block w-full md:hidden"
        shadow="sm"
        radius="md"
        style={{ paddingBottom: insets.bottom }}
      >
        <CardContent className="p-4">
          {hasItems && (
            <View
              className="mb-3 flex flex-row items-center justify-between"
              onClick={() => setDrawerOpen(true)}
            >
              <Typography variant="label-sm">{`Total (${itemCount} Item)`}</Typography>
              <View className="flex flex-row items-center gap-1">
                <Typography color="brand-primary" variant="label-sm">
                  {formatPrice(totalAmount)}
                </Typography>
                <AnimatedCaret isExpanded={drawerOpen} />
              </View>
            </View>
          )}
          <View className="flex flex-col py-0">
            <Button
              variant="primary"
              color="CTA"
              loading={isSubmitting}
              iconAlignment="end"
              iconName="arrowRight"
              onClick={onSubmit}
              className="w-full"
            >
              Continue
            </Button>
            <Typography
              variant="caption-md"
              color="brand-primary"
              className="mx-auto mt-2 max-w-72"
              align="center"
            >
              <Typography
                variant="caption-md"
                color="foreground-intermediate"
                align="center"
                className="mt-3"
              >
                Clicking “Continue” will automatically create an invoice and take you to the payment
                page.
              </Typography>
            </Typography>
          </View>
        </CardContent>
      </Card>
      {hasItems && (
        <BottomDrawerModalAnimated open={drawerOpen} onOpenChange={setDrawerOpen}>
          <View className="flex flex-col gap-3">
            <Typography variant="label-sm" color="foreground-default">
              Items
            </Typography>
            <View className="flex flex-row items-center justify-between">
              <View className="flex flex-row items-center gap-2">
                <LuceSparkle className="text-brand-primary size-4" />
                <Typography variant="body-sm" color="foreground-default">
                  Gift Card
                </Typography>
              </View>
              <Typography variant="body-sm" color="foreground-default">
                {`${itemCount} Item (${formatPrice(unitAmount)})`}
              </Typography>
            </View>
            <Divider />
            <Typography variant="label-sm" color="foreground-default">
              Payment Summary
            </Typography>
            <View className="flex flex-row items-center justify-between">
              <Typography variant="body-sm" color="foreground-intermediate">
                Subtotal
              </Typography>
              <Typography variant="body-sm" color="foreground-default">
                {formatPrice(totalAmount)}
              </Typography>
            </View>
            <Divider />
            <View className="flex flex-row items-center justify-between">
              <Typography variant="body-sm" color="foreground-intermediate">
                Total
              </Typography>
              <Typography variant="label-sm" color="brand-primary">
                {formatPrice(totalAmount)}
              </Typography>
            </View>
            <View className="flex flex-col py-0">
              <Button
                variant="primary"
                color="CTA"
                loading={isSubmitting}
                iconAlignment="end"
                iconName="arrowRight"
                onClick={onSubmit}
                className="w-full"
              >
                Continue
              </Button>
              <Typography
                variant="caption-md"
                color="foreground-intermediate"
                className="mx-auto mt-2 max-w-72"
                align="center"
              >
                Clicking “Continue” will automatically create an invoice and take you to the payment
                page.
              </Typography>
            </View>
          </View>
        </BottomDrawerModalAnimated>
      )}
    </>
  );
}
